/**
 * LIMPIAR EL HTML QUE PEGA WORD (y Google Docs, y Outlook).
 *
 * El equipo tiene sus plantillas en un Word y las pega en el editor de email. Word no pega texto:
 * pega un documento entero — `<style>` con cientos de reglas `mso-*`, `class="MsoListParagraph"`,
 * `<o:p>`, comentarios condicionales `<!--[if gte mso 9]>`, listas simuladas con `<p>` y un
 * caracter de viñeta suelto, y `style="font-family:Calibri; margin:0in 0in 8pt"` en CADA nodo.
 *
 * El navegador mete todo eso tal cual en el contentEditable. Lo que se ve entonces es lo que
 * reportó el equipo: las viñetas se rompen, aparecen saltos de más y el texto sale descolocado —
 * y no solo en el editor: eso mismo viaja en el email y le llega así al cliente. Pegar con
 * ctrl+shift+v tampoco sirve porque pega texto plano y se pierde la firma entera.
 *
 * La regla acá es quedarse con la ESTRUCTURA y tirar la PRESENTACIÓN: negritas, cursivas,
 * subrayado, listas, párrafos, links y tablas se conservan; fuentes, tamaños, márgenes, colores
 * de Word y todo el ruido de Office se van. El resultado hereda los estilos de la app, que es
 * justamente lo que hace que se vea igual en el editor y en el correo.
 */

/** Etiquetas que se conservan. El resto se desenvuelve (se queda su contenido). */
const PERMITIDAS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'BR', 'P', 'DIV', 'SPAN',
  'UL', 'OL', 'LI', 'A', 'H1', 'H2', 'H3', 'H4', 'BLOCKQUOTE',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH', 'IMG',
]);

/** Atributos que sobreviven, por etiqueta. Todo lo demás se borra. */
const ATRIBUTOS: Record<string, string[]> = {
  A: ['href', 'target', 'rel'],
  IMG: ['src', 'alt'],
};

/** Un párrafo de Word que en realidad es un ítem de lista. */
const VINETA = /^\s*(·|•|▪|o|-|\*|§|)\s+/;

export function limpiarHtmlPegado(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // 1) Fuera lo que nunca es contenido: hojas de estilo, scripts, meta y los comentarios
  //    condicionales de Office (que traen bloques enteros de markup alternativo).
  doc.querySelectorAll('style, script, meta, link, title').forEach((n) => n.remove());
  const it = doc.createNodeIterator(doc.body, NodeFilter.SHOW_COMMENT);
  const comentarios: Node[] = [];
  for (let n = it.nextNode(); n; n = it.nextNode()) comentarios.push(n);
  comentarios.forEach((n) => n.parentNode?.removeChild(n));

  // 2) Recorre el árbol: desenvuelve lo no permitido y limpia atributos del resto.
  const limpiar = (el: Element): void => {
    [...el.children].forEach(limpiar);
    const tag = el.tagName.toUpperCase();
    // Los `<o:p>`, `<w:...>` y demás namespaces de Office: se van con su contenido vacío.
    if (tag.includes(':') || tag === 'O:P') { el.remove(); return; }
    if (!PERMITIDAS.has(tag)) {
      // Desenvolver, no borrar: adentro está el texto que la persona quiere.
      const padre = el.parentNode;
      if (padre) { while (el.firstChild) padre.insertBefore(el.firstChild, el); padre.removeChild(el); }
      return;
    }
    const permitidos = ATRIBUTOS[tag] ?? [];
    for (const attr of [...el.attributes]) {
      if (!permitidos.includes(attr.name.toLowerCase())) el.removeAttribute(attr.name);
    }
    // Un <span> que ya no aporta nada (sin atributos) solo agrega ruido al HTML final.
    if (tag === 'SPAN' && el.attributes.length === 0) {
      const padre = el.parentNode;
      if (padre) { while (el.firstChild) padre.insertBefore(el.firstChild, el); padre.removeChild(el); }
    }
  };
  [...doc.body.children].forEach(limpiar);

  // 3) Las "listas" de Word son párrafos que empiezan con un caracter de viñeta. Se convierten
  //    en una lista de verdad: si no, cada ítem queda como un párrafo suelto con un punto raro
  //    adelante, que es exactamente lo que se veía descolocado en la captura del equipo.
  const parrafos = [...doc.body.querySelectorAll('p, div')];
  let lista: HTMLUListElement | null = null;
  for (const p of parrafos) {
    const txt = p.textContent ?? '';
    if (VINETA.test(txt) && p.children.length <= 2) {
      const li = doc.createElement('li');
      li.innerHTML = p.innerHTML.replace(VINETA, '');
      if (!lista) { lista = doc.createElement('ul'); p.parentNode?.insertBefore(lista, p); }
      lista.appendChild(li);
      p.remove();
    } else if (txt.trim()) {
      lista = null; // se cortó la racha de viñetas
    }
  }

  // 4) Párrafos vacíos: Word los siembra por todos lados y son los "saltos de más".
  doc.body.querySelectorAll('p, div').forEach((el) => {
    if (!el.textContent?.trim() && !el.querySelector('img, br')) el.remove();
  });

  return doc.body.innerHTML.trim();
}

/**
 * Handler de paste listo para un contentEditable. Inserta el HTML ya limpio.
 * Si el portapapeles no trae HTML (copiar de una terminal, por ejemplo) deja pasar el pegado
 * normal del navegador, que para texto plano ya hace lo correcto.
 */
export function pegarLimpio(e: React.ClipboardEvent<HTMLElement>): boolean {
  const html = e.clipboardData?.getData('text/html');
  if (!html) return false;
  e.preventDefault();
  document.execCommand('insertHTML', false, limpiarHtmlPegado(html));
  return true;
}
